// src/hooks/useUnsavedChangesPrompt.ts
//
// One-liner for forms that want to participate in the cross-cutting
// "you have unsaved changes" guard. The hook stores the latest `label`
// + `save` callback on a ref so callers don't have to memoize them
// (most form components recreate `save` on every render — wiring the
// ref keeps registry churn down to the actual `dirty` transitions).

import { useEffect, useRef } from "react";
import {
  useDirtyFormPromptOptional,
  type DirtyFormEntry,
} from "@/contexts/DirtyFormPromptContext";

export type UseUnsavedChangesPromptArgs = Omit<DirtyFormEntry, "id"> & {
  /** Stable string. Pick something readable like `"org-settings:branding"`. */
  id: string;
};

/**
 * Registers this form with the unsaved-changes registry. Tolerant of
 * being called outside the provider — the hook becomes a no-op so a
 * component can be reused on a page that doesn't host the workspace
 * shell (Storybook, isolated tests).
 *
 * The registered entry's `save` callback is always read through a ref,
 * so each render's freshly-allocated function value is what the modal
 * eventually invokes — no need for callers to wrap `save` in
 * `useCallback`.
 */
export function useUnsavedChangesPrompt(args: UseUnsavedChangesPromptArgs): void {
  const ctx = useDirtyFormPromptOptional();
  const latestRef = useRef<{ label: string; save?: DirtyFormEntry["save"] }>({
    label: args.label,
    save: args.save,
  });
  latestRef.current = { label: args.label, save: args.save };

  useEffect(() => {
    if (!ctx) return;
    ctx.register({
      id: args.id,
      label: latestRef.current.label,
      dirty: args.dirty,
      save: latestRef.current.save
        ? () => latestRef.current.save!()
        : undefined,
    });
    return () => {
      ctx.unregister(args.id);
    };
  }, [ctx, args.id, args.dirty]);
}
